// Pure wire contract shared by Edge and the existing HTMLAudio client.
export const PROGRESSIVE_SPEECH_PROTOCOL = 1 as const;
export const PROGRESSIVE_SPEECH_LIMITS = {
  textChars: 4096,
  clipChars: 320,
  clipCount: 16,
  clipAudioBytes: 2 * 1024 * 1024,
  totalAudioBytes: 16 * 1024 * 1024,
  frameBytes: Math.ceil((2 * 1024 * 1024) / 3) * 4 + 512,
  providerResponseBytes: 6 * 1024 * 1024,
  requestMs: 180_000,
  providerMs: 90_000,
} as const;

export const PROGRESSIVE_SPEECH_ERROR_CODES = [
  "quota_rejected", "quota_failed", "synthesis_failed", "invalid_audio",
  "timeout", "stream_failed", "free_capacity_reached",
] as const;
export type ProgressiveSpeechErrorCode = typeof PROGRESSIVE_SPEECH_ERROR_CODES[number];
export type ProgressiveSpeechEvent =
  | { type: "start"; protocol: 1; clips: number }
  | { type: "clip"; protocol: 1; seq: number; audio_base64: string; cached: boolean; credit_balance_units: number | null }
  | { type: "done"; protocol: 1; clips: number }
  | { type: "error"; protocol: 1; code: ProgressiveSpeechErrorCode };

export class ProgressiveSpeechFailure extends Error {
  constructor(readonly code: ProgressiveSpeechErrorCode) {
    super(code);
    this.name = "ProgressiveSpeechFailure";
  }
}

export interface ProgressiveSpeechSpan { text: string; start: number; end: number }

/** Partition one stable, already normalized text. Never trim or drop a boundary. */
export function partitionProgressiveSpeech(text: string): ProgressiveSpeechSpan[] {
  const limits = PROGRESSIVE_SPEECH_LIMITS;
  if (!text.length || text.length > limits.textChars) {
    throw new ProgressiveSpeechFailure("invalid_audio");
  }
  const spans: ProgressiveSpeechSpan[] = [];
  let start = 0;
  while (start < text.length) {
    let end = Math.min(text.length, start + limits.clipChars);
    if (end < text.length) {
      // Reserve one UTF-16 unit per remaining span for surrogate-safe cuts.
      const slots = limits.clipCount - spans.length;
      const minLength = Math.max(1, text.length - start - (slots - 1) * (limits.clipChars - 1));
      const piece = text.slice(start, end);
      let sentenceEnd = 0;
      for (const match of piece.matchAll(/[.!?…](?:\s+|$)/g)) {
        const candidate = match.index! + match[0].length;
        if (candidate >= minLength) sentenceEnd = candidate;
      }
      if (sentenceEnd) {
        end = start + sentenceEnd;
      } else {
        let wordEnd = 0;
        for (const match of piece.matchAll(/\s+/g)) {
          const candidate = match.index! + match[0].length;
          if (candidate >= minLength) wordEnd = candidate;
        }
        if (wordEnd) end = start + wordEnd;
      }
      // A hard split is necessary for a long word; a code point stays intact.
      const before = text.charCodeAt(end - 1);
      const after = text.charCodeAt(end);
      if (before >= 0xd800 && before <= 0xdbff && after >= 0xdc00 && after <= 0xdfff) end--;
    }
    if (end <= start || spans.length >= limits.clipCount) {
      throw new ProgressiveSpeechFailure("invalid_audio");
    }
    spans.push({ text: text.slice(start, end), start, end });
    start = end;
  }
  return spans;
}
